import type { Product } from '../services/product.service'
import type { Item3D } from '../types/optimization'
import { createCargoItemFromPreset } from './cargoPreset'
import { convertMeasurement, parseMeasurementUnit } from './measurement'

export interface CatalogProductSelection {
  product: Product
  quantity: number
}

export const validCargoQuantity = (value: number) => Number.isSafeInteger(value) && value > 0

export function catalogDefaultQuantity(product: Product): number {
  const quantity = Number(product.quantity)
  return validCargoQuantity(quantity) ? quantity : 1
}

/** Explain why a catalogue record cannot safely be copied into the current load. */
export function catalogProductIssue(product: Product): string | null {
  if (product.id == null || !String(product.id).trim()) return 'Identifiant du produit manquant.'
  if (product.status && product.status !== 'active') return 'Produit non actif dans le catalogue.'
  for (const [field, label] of [['length', 'Longueur'], ['width', 'Largeur'], ['height', 'Hauteur']] as const) {
    const value = Number(product[field])
    if (!Number.isFinite(value) || value <= 0) return `${label} manquante ou invalide dans la fiche produit.`
  }
  if (product.weight == null || String(product.weight).trim() === '' || !Number.isFinite(Number(product.weight)) || Number(product.weight) < 0) {
    return 'Poids manquant ou invalide dans la fiche produit.'
  }
  try {
    convertMeasurement(1, parseMeasurementUnit(product.dimension_unit || 'mm'), 'mm')
    convertMeasurement(1, parseMeasurementUnit(product.weight_unit || 'kg'), 'kg')
  } catch {
    return 'Unités de dimensions ou de poids inconnues ou incompatibles.'
  }
  return null
}

/** Validate the whole selection before adding anything. Existing lines and catalogue records stay intact. */
export function createCargoItemsFromCatalog(
  selection: CatalogProductSelection[],
  existingItems: Item3D[],
  createId: () => string,
  colors: readonly string[],
): Item3D[] {
  if (!selection.length) throw new Error('Sélectionnez au moins un produit.')
  const seen = new Set<string>()
  for (const { product, quantity } of selection) {
    const issue = catalogProductIssue(product)
    if (issue) throw new Error(`${product.sku || product.name} : ${issue}`)
    if (!validCargoQuantity(quantity)) throw new Error(`${product.sku || product.name} : saisissez une quantité entière supérieure à zéro.`)
    const id = String(product.id)
    if (seen.has(id)) throw new Error('Un produit apparaît plusieurs fois dans la sélection.')
    seen.add(id)
  }
  const added: Item3D[] = []
  for (const { product, quantity } of selection) {
    added.push(createCargoItemFromPreset(
      { ...product, quantity }, [...existingItems, ...added], createId,
      colors[(existingItems.length + added.length) % colors.length] || '#2563eb',
    ))
  }
  return added
}
